import React, { useState } from 'react';
import { Lock, Check } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { useUpdatePasswordMutation } from '../../../store/services/api';
import { toast } from 'react-toastify';

const SecuritySettings = () => {
  const [updatePassword, { isLoading: isSubmitting }] = useUpdatePasswordMutation();
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user types
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const { currentPassword, newPassword, confirmNewPassword } = formData;

    // Validations
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    
    if (!validatePassword(newPassword)) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      await updatePassword({
        current_password: currentPassword,
        new_password: newPassword,
      }).unwrap();
      
      // Clear form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      
      // Show success message
      setSuccess('Password updated successfully');
      toast.success('Password updated successfully');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to update password';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <h1 className="text-xl font-semibold mb-1">Security Settings</h1>
        <p className="text-sm text-gray-500">Manage your account security and password</p>
      </div>

      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4 text-[#FF4444]" />
          <h2 className="text-base font-medium">Change Password</h2>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-2.5 rounded-md mb-4 text-xs">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-600 p-2.5 rounded-md mb-4 text-xs flex items-center">
            <Check className="w-3.5 h-3.5 mr-1.5" />
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Current Password
            </label>
            <input
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all text-sm"
              placeholder="Enter current password"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all text-sm"
              placeholder="Enter new password"
            />
            <p className="mt-1 text-xs text-gray-500">Password must be at least 8 characters long</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirmNewPassword"
              value={formData.confirmNewPassword}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all text-sm"
              placeholder="Confirm new password"
            />
          </div>

          <div>
            <Button 
              type="submit" 
              className="bg-[#FF4444] text-white hover:bg-[#FF3333] w-full h-9 text-sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecuritySettings;